import React, { useEffect, useState } from "react";
import {
  addNewDoc,
  getAllDocsSnap,
} from "../../../utils/firebase/Firebase.utils";
import InputBox from "../Components/InputBox";
import SelectInput from "../Components/SelectInput";
import NumberInputText from "../Components/NumberInputText";
import toast from "react-hot-toast";
import {
  FaUser,
  FaFileInvoice,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaTimes,
} from "react-icons/fa";
import { motion } from "framer-motion";

export default function BillingForm({ setAddMode }) {
  const [residents, setResidents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const billingTypes = ["Electricity", "Water", "Association Dues", "Others"];
  const billingTypeOptions = billingTypes.map((type) => {
    return {
      value: type,
      label: type,
    };
  });

  useEffect(() => {
    const fetchResidents = async () => {
      await getAllDocsSnap("residents", (data) => {
        data.sort((a, b) => {
          if (a.owner1 < b.owner1) return -1;
          if (a.owner1 > b.owner1) return 1;
          return 0;
        });
        setResidents(data);
        setIsLoading(false);
      });
    };
    fetchResidents();
  }, []);

  const identifierOptions = residents.map((resident) => {
    return {
      value: resident.identifier,
      label: `${resident.owner1} (${resident.identifier})`,
      name: resident.owner1,
    };
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const removeCommas = (num) => {
    return num.replace(/,/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = removeCommas(formData.amount);
    setIsProcessing(true);

    await addNewDoc("billings", {
      ...formData,
      billingAmount: amount,
      status: "unpaid",
    });
    toast.success("Billing created successfully");
    setIsProcessing(false);
    setAddMode(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="page relative bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-[#363062]">
          Create New Billing
        </h2>
        <button
          onClick={() => setAddMode(false)}
          className="text-gray-500 hover:text-[#363062] transition-colors"
        >
          <FaTimes size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaUser className="mr-2 text-[#363062]" />
              Select Resident
            </label>
            <SelectInput
              options={identifierOptions}
              required
              type="text"
              onChange={(e) =>
                setFormData({ ...formData, identifier: e.value, name: e.name })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaFileInvoice className="mr-2 text-[#363062]" />
              Billing Type
            </label>
            <SelectInput
              options={billingTypeOptions}
              required
              type="text"
              onChange={(e) =>
                setFormData({ ...formData, billingType: e.value })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaFileInvoice className="mr-2 text-[#363062]" />
              SOA Number
            </label>
            <InputBox
              type="text"
              name="soaNumber"
              required
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaCalendarAlt className="mr-2 text-[#363062]" />
              Billing Date
            </label>
            <InputBox
              type="date"
              name="billingDate"
              required
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaCalendarAlt className="mr-2 text-[#363062]" />
              Due Date
            </label>
            <InputBox
              type="date"
              name="dueDate"
              required
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FaMoneyBillWave className="mr-2 text-[#363062]" />
              Amount
            </label>
            <NumberInputText
              type="number"
              name="amount"
              placeholder="Enter Amount"
              formData={formData}
              setFormData={setFormData}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <FaFileInvoice className="mr-2 text-[#363062]" />
            Description
          </label>
          <InputBox
            name="description"
            placeholder="Enter Description/Particulars"
            required
            type="text"
            onChange={handleChange}
          />
        </div>

        <div className="flex justify-end mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isProcessing}
            className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${
              isProcessing
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#363062] hover:bg-[#2a2450]"
            }`}
          >
            {isProcessing ? "Processing..." : "Save Billing"}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
}
